import React from 'react';
import { Dialog as MuiDialog, Box, DialogContent, Divider, useTheme, useMediaQuery } from '@mui/material';
import { DialogActionsLayout, DialogActionsLayoutProps } from './components/DialogActionsLayout';
import { DialogHeader, DialogHeaderProps } from './components/DialogHeader';

export interface DialogProps {
  isOpen: boolean;
  variant?: 'modal' | 'banner';
  children?: React.ReactNode;
  customWidth?: string;
  hasDivider?: boolean;
  dialogHeaderIcon?: React.ReactNode;
  dialogHeaderProps?: DialogHeaderProps;
  dialogActionProps?: DialogActionsLayoutProps;
  additionalContent?: React.ReactNode;
}

/**
 * Renders the Dialog component used for Confirmation, Alert, or form modals.
 * Use variant="banner" for the bottom-anchored cookie/notification banner style.
 */
export const Dialog: React.FC<React.PropsWithChildren<DialogProps>> = ({
  isOpen,
  variant = 'modal',
  children,
  hasDivider = false,
  dialogHeaderIcon,
  dialogHeaderProps,
  dialogActionProps,
  customWidth,
  additionalContent,
}: DialogProps) => {
  const {
    breakpoints,
    custom: { spacing },
  } = useTheme();
  const isSmallDevice = useMediaQuery(breakpoints.down('xs'));
  const isSmallDeviceSm = useMediaQuery(breakpoints.down('sm'));

  if (variant === 'banner') {
    return (
      <MuiDialog
        open={isOpen}
        hideBackdrop
        disableScrollLock
        PaperProps={{
          sx: {
            position: 'absolute',
            bottom: 0,
            mb: isSmallDeviceSm ? 0 : 4,
            mx: 0,
            maxWidth: `${breakpoints.values.sm}px`,
            width: '100%',
            borderRadius: '16px',
          },
        }}
      >
        {children}
      </MuiDialog>
    );
  }

  return (
    <MuiDialog
      open={isOpen}
      aria-labelledby="modal-title"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          p: 0,
        },
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            maxWidth: '100%',
            width: customWidth || '440px',
          },
        },
      }}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          dialogHeaderProps?.onClose();
        }
      }}
    >
      <DialogHeader {...dialogHeaderProps} onClose={dialogHeaderProps?.onClose || (() => {})} icon={dialogHeaderIcon} />
      {hasDivider && <Divider sx={{ color: '#EAECF0', mt: spacing?.['2xl'] }} />}
      <Box
        sx={{
          overflow: 'auto',
          zIndex: 1,
        }}
      >
        {children && (
          <DialogContent data-testid="dialog-content">
            <Box
              sx={{
                pt: spacing?.['2xl'],
                px: isSmallDevice ? spacing?.['xl'] : spacing?.['3xl'],
                pb: isSmallDevice ? spacing?.['3xl'] : spacing?.['4xl'],
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            >
              {children}
            </Box>
          </DialogContent>
        )}
      </Box>
      {additionalContent && additionalContent}
      <DialogActionsLayout {...dialogActionProps} />
    </MuiDialog>
  );
};
